import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import React from 'react';

const StyledInputField = styled(TextField)(({ theme }) => {
  const tokens = theme.componentSizes.inputField.md;
  return {
    '& .MuiInputBase-root': {
      height: tokens.height,
      minHeight: tokens.height,
      borderRadius: tokens.borderRadius,
      fontWeight: 400,
      fontSize: tokens.fontSize,
      color: theme.palette.inputField.font,
      background: theme.palette.inputField.background,
      lineHeight: '21px',
      boxSizing: 'border-box',
      letterSpacing: '0px',
      padding: tokens.padding,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.inputField.border,
      borderWidth: 1,
      borderRadius: tokens.borderRadius,
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.inputField.focusBorder,
      borderWidth: 1,
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.inputField.hoverBorder,
    },
    '& .Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.inputField.errorBorder,
    },
    '& input': {
      fontWeight: 400,
      fontSize: tokens.fontSize,
      color: theme.palette.inputField.font,
      lineHeight: '21px',
      letterSpacing: '0px',
      padding: 0,
      '&::placeholder': {
        color: theme.palette.inputField.placeholder,
        opacity: 1,
      },
      // Hide number input spinners/arrows
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&[type=number]': {
        MozAppearance: 'textfield',
      },
    },
    '&.Mui-focused': {
      outline: 'none',
    },
  };
});

interface InputFieldProps {
  label?: string;
  value: string;
  onChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  sx?: object;
  InputProps?: object;
  type?: 'text' | 'number' | 'password' | 'email';
  inputProps?: object;
  bottomLabel?: string;
  [key: string]: any;
}

const InputField: React.FC<InputFieldProps> = React.memo(function InputField({
  label,
  value = '',
  onChange,
  placeholder = 'Type here...',
  disabled = false,
  error = false,
  helperText,
  startAdornment,
  endAdornment,
  sx = {},
  type = 'text',
  inputProps,
  bottomLabel,
  ...props
}) {
  return (
    <Box sx={{ width: '100%', ...sx }}>
      <FormControl variant="outlined" size="medium" fullWidth disabled={disabled} error={error}>
        {label && (
          <InputLabel
            shrink={false}
            sx={{
              position: 'static',
              transform: 'none',
              marginBottom: (theme) => theme.spacing(1),
              fontWeight: 500,
              fontSize: '14px',
              color: (theme) => theme.palette.formField.label,
              lineHeight: '20px',
            }}
          >
            {label}
          </InputLabel>
        )}
        <StyledInputField
          variant="outlined"
          fullWidth
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          InputProps={{
            startAdornment,
            endAdornment,
            ...props.InputProps,
          }}
          inputProps={inputProps}
          InputLabelProps={{ shrink: false }}
          autoComplete="off"
          {...props}
        />
        {helperText && (
          <Box
            sx={{
              fontSize: '12px',
              color: (theme) =>
                error ? theme.palette.formField.helperError : theme.palette.formField.helperDefault,
              marginTop: (theme) => theme.spacing(0.5),
              marginLeft: '12px',
            }}
          >
            {helperText}
          </Box>
        )}
        {bottomLabel && (
          <InputLabel
            shrink={false}
            sx={{
              position: 'static',
              transform: 'none',
              marginBottom: (theme) => theme.spacing(1),
              fontWeight: 400,
              fontSize: '14px',
              color: (theme) => theme.palette.formField.label,
              lineHeight: '20px',
            }}
          >
            {bottomLabel}
          </InputLabel>
        )}
      </FormControl>
    </Box>
  );
});

export default InputField;
